import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, gql } from '@apollo/client';
import { useAuth } from '../contexts/AuthContext';
import { FiUser, FiHeart, FiPlay, FiArrowLeft, FiMessageSquare, FiCheckSquare, FiChevronDown, FiChevronUp, FiUserX, FiCheck, FiX, FiXCircle } from 'react-icons/fi';
import { formatDate } from '../utils/dateUtils';
import { GET_USER_UNANSWERED_REQUIRED_ARTICLES } from '../graphql/voting';
import { GET_RELATIONSHIP, ACCEPT_FRIEND_REQUEST, REJECT_FRIEND_REQUEST } from '../graphql/friends';
import { CREATE_MATCHING, SKIP_USER } from '../graphql/matching';
import { BAN_USER } from '../graphql/admin';
import VotingCard from '../components/VotingCard';
import VotingCardMobile from '../components/VotingCardMobile';
import VotingDetailModal from '../components/VotingDetailModal';

// GraphQL 查詢
const GET_USER_DETAILS = gql`
  query GetUserDetails($userId: ID!, $relationship: String) {
    user(id: $userId) {
      id
      username
      email
      status
      createdAt
    }
    userProfiles(userId: $userId, relationship: $relationship) {
      success
      profiles {
        id
        contentType
        content
        visibility
        order
      }
    }
    userAvatar(userId: $userId, relationship: $relationship) {
      id
      content
      contentType
    }
  }
`;

const GET_USER_MATCHING_SCORE = gql`
  query GetUserMatchingScore($targetUserId: ID!) {
    calculateUserMatchingScore(targetUserId: $targetUserId) {
      success
      score
      totalWeight
      answeredQuestions
      totalQuestions
      questionMatches {
        questionId
        questionTitle
        questionDescription
        requesterAnswer
        targetAnswer
        questionOptions {
          text
          order
        }
        weight
        score
        isMatch
      }
    }
  }
`;

const GET_USER_DEFAULT_TEST = gql`
  query GetUserDefaultTest($userId: ID!) {
    userDefaultTest(userId: $userId) {
      id
      name
      description
      questionSet {
        id
        name
      }
    }
  }
`;

const UserDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  
  const [showTestModal, setShowTestModal] = useState(false);
  const [showUnansweredQuestions, setShowUnansweredQuestions] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedVotingId, setSelectedVotingId] = useState(null);
  const [collapsedQuestionMatches, setCollapsedQuestionMatches] = useState(false);
  const itemsPerPage = 15;

  // 獲取與該使用者的關係
  const { data: relationshipData, refetch: refetchRelationship } = useQuery(GET_RELATIONSHIP, {
    variables: { targetUserId: userId },
    skip: !userId || !currentUser || currentUser.id === userId
  });
  
  // Mutations
  const [createMatching, { loading: createMatchingLoading }] = useMutation(CREATE_MATCHING, {
    onCompleted: (data) => {
      if (data.createMatching.success) {
        alert(data.createMatching.message);
        refetchRelationship();
      } else {
        alert('發送失敗：' + data.createMatching.message);
      }
    },
    onError: (error) => {
      console.error('發送配對請求失敗:', error);
      alert('發送配對請求失敗');
    }
  });

  const [skipUser, { loading: skipUserLoading }] = useMutation(SKIP_USER, {
    onCompleted: (data) => {
      if (data.skipUser.success) {
        alert(data.skipUser.message || '已跳過該使用者');
        navigate('/matching'); // 返回配對頁面
      } else {
        alert('跳過失敗：' + data.skipUser.message);
      }
    },
    onError: (error) => {
      console.error('跳過使用者失敗:', error);
      alert('跳過使用者失敗');
    }
  });
  
  const [acceptFriendRequest, { loading: acceptLoading }] = useMutation(ACCEPT_FRIEND_REQUEST, {
    onCompleted: (data) => {
      if (data.acceptFriendRequest.success) {
        alert(data.acceptFriendRequest.message);
        refetchRelationship();
      } else {
        alert('接受失敗：' + data.acceptFriendRequest.message);
      }
    },
    onError: (error) => {
      console.error('接受好友請求失敗:', error);
      alert('接受好友請求失敗');
    }
  });
  
  const [rejectFriendRequest, { loading: rejectLoading }] = useMutation(REJECT_FRIEND_REQUEST, {
    onCompleted: (data) => {
      if (data.rejectFriendRequest.success) {
        alert(data.rejectFriendRequest.message);
        refetchRelationship();
      } else {
        alert('操作失敗：' + data.rejectFriendRequest.message);
      }
    },
    onError: (error) => {
      console.error('拒絕好友請求失敗:', error);
      alert('拒絕好友請求失敗');
    }
  });

  // 決定關係字串
  const determineRelationship = () => {
    // 如果是自己，返回 'self' (但後端會處理)
    if (currentUser && currentUser.id === userId) {
      return 'self';
    }
    
    // 如果沒有登入或沒有關係資料，使用 'public'
    if (!currentUser || !relationshipData?.relationship?.relationship) {
      return 'public';
    }
    
    const rel = relationshipData.relationship.relationship;
    const isUserA = rel.userA.id === currentUser.id;
    const relationType = isUserA ? rel.typeA : rel.typeB;
    
    // 根據關係類型返回相應字串
    if (relationType === 'CLOSE_FRIEND') {
      return 'close_friend';
    } else if (relationType === 'FRIEND') {
      return 'friend';
    }
    
    return 'public';
  };

  const relationshipString = determineRelationship();

  // 獲取使用者詳細資料
  const { data: userData, loading: userLoading, error: userError } = useQuery(GET_USER_DETAILS, {
    variables: { userId, relationship: relationshipString },
    skip: !userId
  });

  // 獲取配對得分
  const { data: matchingData, loading: matchingLoading, error: matchingError } = useQuery(GET_USER_MATCHING_SCORE, {
    variables: { targetUserId: userId },
    skip: !userId || !currentUser
  });

  // 獲取使用者的預設測試
  const { data: testData, loading: testLoading } = useQuery(GET_USER_DEFAULT_TEST, {
    variables: { userId },
    skip: !userId
  });

  // 獲取尚未作答的必答題
  const { data: unansweredData, loading: unansweredLoading, refetch: refetchUnanswered } = useQuery(
    GET_USER_UNANSWERED_REQUIRED_ARTICLES,
    {
      variables: { 
        userId,
        limit: itemsPerPage,
        skip: (currentPage - 1) * itemsPerPage
      },
      skip: !userId || !currentUser || !showUnansweredQuestions,
      fetchPolicy: 'cache-and-network'
    }
  );

  const user = userData?.user;
  const profiles = userData?.userProfiles?.profiles || [];
  const avatar = userData?.userAvatar;
  const matchingScore = matchingData?.calculateUserMatchingScore;
  const defaultTest = testData?.userDefaultTest;
  
  // Ban/Unban mutation
  const [banUser, { loading: banLoading }] = useMutation(BAN_USER, {
    onCompleted: (data) => {
      if (data.banUser.success) {
        alert(data.banUser.message);
        // 刷新使用者數據
        window.location.reload();
      } else {
        alert('操作失敗：' + data.banUser.message);
      }
    },
    onError: (error) => {
      console.error('Ban user error:', error);
      alert('操作失敗：' + error.message);
    }
  });
  
  // 檢查是否是半管理員
  const isSemiAdmin = currentUser?.isSemiAdmin || false;
  
  // 處理ban/解ban
  const handleBanUser = async () => {
    if (!user) return;
    const shouldBan = user.status !== 'banned';
    if (window.confirm(`確定要${shouldBan ? '禁用' : '解除禁用'}使用者 ${user.username} 嗎？`)) {
      await banUser({
        variables: {
          userId: userId,
          shouldBan: shouldBan
        }
      });
    }
  };

  const handleStartTest = () => {
    if (defaultTest) {
      navigate(`/test/${defaultTest.id}`);
    }
  };

  const handleShowUnansweredQuestions = () => {
    setShowUnansweredQuestions(true);
    setCurrentPage(1);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleVoteSuccess = () => {
    // 投票成功後刷新列表
    refetchUnanswered();
  };

  const handleViewDetail = (votingId) => {
    setSelectedVotingId(votingId);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // 處理發送配對請求
  const handleSendMatchingRequest = async () => {
    if (!userId) return;
    await createMatching({ variables: { targetUserId: userId } });
  };

  // 處理跳過使用者
  const handleSkipUser = async () => {
    if (!userId) return;
    await skipUser({ variables: { targetUserId: userId } });
  };
  
  // 處理接受好友請求
  const handleAcceptRequest = async () => {
    if (!userId) return;
    await acceptFriendRequest({ variables: { targetUserId: userId } });
  };
  
  // 處理拒絕/取消請求
  const handleRejectRequest = async () => {
    if (!userId) return;
    const relationship = relationshipData?.relationship?.relationship;
    if (!relationship) return;
    
    const myType = relationship.typeA; // getRelationship已經swap過，當前使用者永遠是userA
    const confirmMessage = myType === 'PENDING' 
      ? '確定要取消配對請求嗎？' 
      : '確定要拒絕配對請求嗎？';
    
    if (window.confirm(confirmMessage)) {
      await rejectFriendRequest({ variables: { targetUserId: userId } });
    }
  };
  
  // 處理開始聊天
  const handleStartChat = () => {
    navigate('/friends');
  };
  
  // 判斷關係狀態並返回對應的按鈕
  const renderRelationshipButton = () => {
    if (!currentUser || currentUser.id === userId) return null;
    
    const relationship = relationshipData?.relationship?.relationship;
    
    // 無關係 - 顯示發送配對請求按鈕和跳過按鈕
    if (!relationship) {
      return (
        <div className="flex items-center space-x-2">
          <button 
            onClick={handleSendMatchingRequest}
            disabled={createMatchingLoading}
            className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-pink-400 to-purple-500 text-white font-medium rounded-lg hover:from-pink-500 hover:to-purple-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiHeart className="w-4 h-4 mr-2" />
            {createMatchingLoading ? '發送中...' : '發送配對請求'}
          </button>
          <button 
            onClick={handleSkipUser}
            disabled={skipUserLoading}
            className="inline-flex items-center px-4 py-2 bg-gray-500 text-white font-medium rounded-lg hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiX className="w-4 h-4 mr-2" />
            {skipUserLoading ? '跳過中...' : '跳過'}
          </button>
        </div>
      );
    }
    
    const myType = relationship.typeA; // getRelationship已經swap過，當前使用者永遠是userA
    const otherType = relationship.typeB;
    
    // 我發送了請求 (PENDING) - 顯示取消請求按鈕
    if (myType === 'PENDING' && otherType === 'INVITED') {
      return (
        <button 
          onClick={handleRejectRequest}
          disabled={rejectLoading}
          className="inline-flex items-center px-4 py-2 bg-orange-500 text-white font-medium rounded-lg hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FiXCircle className="w-4 h-4 mr-2" />
          {rejectLoading ? '取消中...' : '取消請求'}
        </button>
      );
    }
    
    // 我收到了請求 (INVITED) - 顯示接受和拒絕按鈕
    if (myType === 'INVITED' && otherType === 'PENDING') {
      return (
        <div className="flex items-center space-x-2">
          <button 
            onClick={handleAcceptRequest}
            disabled={acceptLoading}
            className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-400 to-blue-500 text-white font-medium rounded-lg hover:from-green-500 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiCheck className="w-4 h-4 mr-2" />
            {acceptLoading ? '接受中...' : '接受請求'}
          </button>
          <button 
            onClick={handleRejectRequest}
            disabled={rejectLoading}
            className="inline-flex items-center px-4 py-2 bg-gray-500 text-white font-medium rounded-lg hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiX className="w-4 h-4 mr-2" />
            {rejectLoading ? '拒絕中...' : '拒絕請求'}
          </button>
        </div>
      );
    }
    
    // 已是好友 (FRIEND) - 顯示開始聊天按鈕
    if (myType === 'FRIEND' && otherType === 'FRIEND') {
      return (
        <button 
          onClick={handleStartChat}
          className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-400 to-indigo-500 text-white font-medium rounded-lg hover:from-blue-500 hover:to-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <FiMessageSquare className="w-4 h-4 mr-2" />
          開始聊天
        </button>
      );
    }
    
    // 其他狀態 - 不顯示按鈕
    return null;
  };

  if (userLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">載入使用者資料中...</p>
        </div>
      </div>
    );
  }

  if (userError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">載入使用者資料失敗</p>
          <button 
            onClick={handleBack}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            返回
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">使用者不存在</p>
          <button 
            onClick={handleBack}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            返回
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-2 md:px-4 py-8">
        {/* 返回按鈕 */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={handleBack}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <FiArrowLeft className="w-4 h-4 mr-2" />
            返回
          </button>
        </div>

        {/* 使用者資料卡片 */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          {/* 第一行：使用者名 */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <h1 className="text-2xl font-bold text-gray-900">{user.username}</h1>
              {user.status === 'banned' && (
                <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">
                  已禁用
                </span>
              )}
            </div>
            {/* 半管理員ban按鈕 */}
            {isSemiAdmin && currentUser && currentUser.id !== userId && (
              <button
                onClick={handleBanUser}
                disabled={banLoading}
                className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg ${
                  user.status === 'banned'
                    ? 'bg-green-500 text-white hover:bg-green-600'
                    : 'bg-red-500 text-white hover:bg-red-600'
                } disabled:opacity-50`}
              >
                <FiUserX className="w-4 h-4 mr-2" />
                {banLoading ? '處理中...' : (user.status === 'banned' ? '解除禁用' : '禁用帳號')}
              </button>
            )}
          </div>
          
          {/* 根據關係狀態顯示不同按鈕 */}
          <div className="mb-4">
            {renderRelationshipButton()}
          </div>
          
          {/* 第二行及以下：其他信息靠左 */}
          <div>
            <p className="text-gray-600 mb-4">
              加入時間：{user.createdAt ? formatDate(user.createdAt) : '未知'}
            </p>
            
            {/* 自我介紹 */}
            {profiles.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">自我介紹</h3>
                <div className="space-y-2">
                  {profiles.map((profile) => (
                    <div key={profile.id} className="bg-gray-50 rounded-lg p-3">
                      {profile.contentType === 'TEXT' ? (
                        <p className="text-gray-700 whitespace-pre-wrap">{profile.content}</p>
                      ) : (
                        <div className="space-y-2">
                          <img
                            src={profile.content}
                            alt="個人圖片"
                            className="max-w-full h-auto rounded-lg shadow-sm"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'block';
                            }}
                          />
                          <div className="hidden bg-gray-100 rounded-lg p-4 text-center">
                            <svg className="mx-auto h-8 w-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <p className="text-sm text-gray-500">圖片載入失敗</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 配對得分卡片 */}
        {currentUser && currentUser.id !== userId && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">配對分析</h2>
            
            {matchingLoading ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">計算配對得分中...</p>
              </div>
            ) : matchingError ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-600">計算配對得分失敗</p>
              </div>
            ) : matchingScore ? (
              <div className="space-y-4">
                {/* 分數詳情 */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm text-gray-600">得分</div>
                    <div className="text-xl font-semibold text-purple-600">{matchingScore.score.toFixed(2)}</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm text-gray-600">總權重</div>
                    <div className="text-xl font-semibold text-purple-600">{matchingScore.totalWeight}</div>
                  </div>
                </div>

                {/* 對方的回答 */}
                {matchingScore.questionMatches && matchingScore.questionMatches.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-lg font-semibold text-gray-900">對方的回答</h4>
                      <button
                        onClick={() => setCollapsedQuestionMatches(!collapsedQuestionMatches)}
                        className="flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
                      >
                        {collapsedQuestionMatches ? (
                          <>
                            <span className="mr-1">展開</span>
                            <FiChevronDown className="w-4 h-4" />
                          </>
                        ) : (
                          <>
                            <span className="mr-1">收起</span>
                            <FiChevronUp className="w-4 h-4" />
                          </>
                        )}
                      </button>
                    </div>
                    {!collapsedQuestionMatches && (
                      <div className="space-y-2">
                        {matchingScore.questionMatches.map((match, index) => {
                          // 獲取回答的選項文字
                          const getAnswerText = (answerIndex) => {
                            if (answerIndex === -1) return '未作答';
                            const option = match.questionOptions?.find(opt => opt.order === answerIndex);
                            return option ? option.text : `選項 ${answerIndex + 1}`;
                          };
                          
                          return (
                            <div key={index} className="bg-gray-50 rounded-lg p-3">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-base font-medium text-gray-700 break-words">{match.questionTitle}</span>
                                <span className={`text-sm font-semibold whitespace-nowrap ml-2 flex-shrink-0 ${match.isMatch ? 'text-purple-600' : 'text-gray-900'}`}>
                                  {match.isMatch ? '匹配' : '不匹配'}
                                </span>
                              </div>
                              {match.questionDescription && (
                                <div className="text-sm text-gray-600 mb-2 break-words">
                                  {match.questionDescription}
                                </div>
                              )}
                              <div className="text-sm text-purple-600 mb-2 break-words">
                                回答: {getAnswerText(match.targetAnswer)}
                              </div>
                              <div className="text-sm text-gray-500">
                                權重: {match.weight} | 得分: <span className={match.score === -9999 ? 'text-red-600 font-bold' : ''}>
                                  {typeof match.score === 'number' ? (match.score === -9999 ? '-9999' : match.score.toFixed(2)) : match.score}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-gray-600">無法計算配對得分</p>
              </div>
            )}
          </div>
        )}

        {/* 補答必答題按鈕 */}
        {defaultTest && currentUser && currentUser.id !== userId && !showUnansweredQuestions && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">未作答必答題</h2>
            <p className="text-gray-600 mb-4">
              完成必答題可以提高你們的配對分數，更深入瞭解彼此的價值觀和興趣。
            </p>
            
            <button
              onClick={handleShowUnansweredQuestions}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-400 to-indigo-500 text-white font-medium rounded-lg hover:from-blue-500 hover:to-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <FiCheckSquare className="w-5 h-5 mr-2" />
              補答必答題
            </button>
          </div>
        )}

        {/* 尚未作答的必答題列表 */}
        {showUnansweredQuestions && currentUser && currentUser.id !== userId && (
          <div className="mt-8 bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {user.username} 的尚未作答必答題
                </h2>
                <p className="text-gray-600">
                  完成這些必答題可以提高你們的配對分數
                </p>
              </div>
              <button
                onClick={() => setShowUnansweredQuestions(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium"
              >
                隱藏
              </button>
            </div>

            {unansweredLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : unansweredData?.userUnansweredRequiredArticles?.articles?.length > 0 ? (
              <>
                {/* 統計資訊 */}
                <div className="mb-6">
                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-purple-600">
                        {unansweredData.userUnansweredRequiredArticles.totalCount}
                      </div>
                      <div className="text-sm text-blue-800 mt-1">
                        道尚未作答的必答題
                      </div>
                    </div>
                  </div>
                </div>

                {/* 投票列表 */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {unansweredData.userUnansweredRequiredArticles.articles.map((article) => (
                    <React.Fragment key={article.id}>
                      {/* 桌面端顯示 */}
                      <div className="hidden md:block">
                        <VotingCard
                          article={article}
                          onVoteSuccess={handleVoteSuccess}
                          onViewDetail={handleViewDetail}
                        />
                      </div>
                      {/* 移動端顯示 */}
                      <div className="md:hidden">
                        <VotingCardMobile
                          article={article}
                          onVoteSuccess={handleVoteSuccess}
                          onViewDetail={handleViewDetail}
                        />
                      </div>
                    </React.Fragment>
                  ))}
                </div>

                {/* 分頁組件 */}
                {unansweredData.userUnansweredRequiredArticles.totalCount > itemsPerPage && (
                  <div className="mt-8">
                    <div className="flex justify-center items-center space-x-2">
                      {/* 上一頁按鈕 */}
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`px-3 py-2 rounded-lg ${
                          currentPage === 1
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        上一頁
                      </button>

                      {/* 頁碼顯示 */}
                      <div className="px-4 py-2 text-gray-700">
                        第 {currentPage} 頁 / 共 {Math.ceil(unansweredData.userUnansweredRequiredArticles.totalCount / itemsPerPage)} 頁
                      </div>

                      {/* 下一頁按鈕 */}
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage >= Math.ceil(unansweredData.userUnansweredRequiredArticles.totalCount / itemsPerPage)}
                        className={`px-3 py-2 rounded-lg ${
                          currentPage >= Math.ceil(unansweredData.userUnansweredRequiredArticles.totalCount / itemsPerPage)
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        下一頁
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <div className="text-gray-500 text-lg mb-4">
                  🎉 太棒了！你已經完成了所有必答題
                </div>
                <p className="text-gray-600">
                  你已經回答了 {user.username} 的所有必答題
                </p>
              </div>
            )}
          </div>
        )}

        {/* 投票詳情彈窗 */}
        <VotingDetailModal
          isOpen={selectedVotingId !== null}
          onClose={() => setSelectedVotingId(null)}
          votingId={selectedVotingId}
        />
      </div>
    </div>
  );
};

export default UserDetail; 